import { FC } from "react";

import Box from "@mui/material/Box";
import { darken, SxProps, Theme, useTheme } from "@mui/material/styles";
import { ChartsTooltipContainer } from "@mui/x-charts";
import { ChartDataProvider } from "@mui/x-charts/ChartDataProvider";
import { ChartsOverlay } from "@mui/x-charts/ChartsOverlay";
import { ChartsSurface } from "@mui/x-charts/ChartsSurface";
import { ChartsWrapper } from "@mui/x-charts/ChartsWrapper";
import { PiePlot } from "@mui/x-charts/PieChart";

import { getBackgroundColor, getFadedColor } from "./ChartColors";
import { DEFAULT_CHART_SIZE, DEFAULT_PADDING_AROUND_DONUT_CHART } from "./defaults";
import { DonutChartTooltip } from "./DonutChartTooltip";
import { ChartDataPoint } from "./types";

import { mergeSx } from "@/common/helpers/mergeSx";
import { getSkeletonColor, skeletonPulseAnimation } from "@/dashboard-design-system/Skeleton";

const DONUT_THICKNESS = 40;

const calculateOuterRadius = (size: number) => {
  return size / 2;
};

const calculateInnerRadius = (size: number, thickness: number) => {
  return calculateOuterRadius(size) - thickness;
};

type DashboardDonutChartProps = {
  dataPoints: ChartDataPoint[];
  size?: number;
  thickness?: number;
  activeDataPointId?: ChartDataPoint["id"];
  onSegmentClick?: (dataPoint: ChartDataPoint, event: React.MouseEvent<SVGPathElement>) => void;
  collapseMargins?: boolean;
  sx?: SxProps<Theme>;
};

export const DashboardDonutChart = ({
  dataPoints,
  size = DEFAULT_CHART_SIZE,
  thickness = DONUT_THICKNESS,
  activeDataPointId,
  onSegmentClick,
  collapseMargins = false,
  sx,
}: DashboardDonutChartProps) => {
  const theme = useTheme();
  const outerRadius = calculateOuterRadius(size);
  const innerRadius = calculateInnerRadius(size, thickness);

  const isEmpty = dataPoints.length === 0 || dataPoints.every((dp) => dp.value === 0);
  const compact = dataPoints.length >= 20;

  const dataPointsOrEmpty = isEmpty
    ? [{ id: "empty", label: "Empty", value: 1, color: getBackgroundColor(theme) }]
    : dataPoints;

  const styles = mergeSx({
    "& .MuiPieArc-root": {
      strokeWidth: isEmpty || compact ? 0 : 2,
      cursor: isEmpty ? "default" : "pointer",
    },
    ...(!isEmpty && {
      ...Object.assign(
        {},
        ...dataPoints.map((dp, i) => ({
          [`& .MuiPieArc-data-index-${i}`]: {
            fill: activeDataPointId ? (activeDataPointId === dp.id ? dp.color : getFadedColor(theme)) : dp.color,
            ["&:hover"]: {
              fill: darken(dp.color, 0.3),
            },
          },
        }))
      ),
    }),
  });

  const calculatedSize = size + DEFAULT_PADDING_AROUND_DONUT_CHART * 2; // Leave padding around the chart so that the tooltip displays properly

  return (
    <ChartDataProvider
      xAxis={[{ position: "none" }]}
      yAxis={[{ position: "none" }]}
      height={calculatedSize}
      series={[
        {
          type: "pie",
          data: dataPointsOrEmpty,
          innerRadius,
          outerRadius,
        },
      ]}
    >
      <ChartsWrapper
        sx={mergeSx(
          {
            width: calculatedSize,
            height: calculatedSize,
            flex: "0 0 auto",
            ...(collapseMargins && {
              margin: `${-DEFAULT_PADDING_AROUND_DONUT_CHART}px`, // Donut charts need extra spacing around the chart to display the tooltip properly
            }),
          },
          sx
        )}
      >
        <ChartsSurface sx={styles}>
          <PiePlot
            onItemClick={(e, itemIdentifier) =>
              isEmpty ? undefined : onSegmentClick?.(dataPoints[itemIdentifier.dataIndex], e)
            }
          />
          <ChartsOverlay />
        </ChartsSurface>
        {!isEmpty && (
          <ChartsTooltipContainer trigger="item">
            <DonutChartTooltip dataPoints={dataPoints} />
          </ChartsTooltipContainer>
        )}
      </ChartsWrapper>
    </ChartDataProvider>
  );
};

type DashboardDonutChartSkeletonProps = {
  size?: number;
  thickness?: number;
  sx?: SxProps<Theme>;
  collapseMargins?: boolean;
};

export const DashboardDonutChartSkeleton: FC<DashboardDonutChartSkeletonProps> = ({
  size = DEFAULT_CHART_SIZE,
  thickness = DONUT_THICKNESS,
  collapseMargins = false,
  sx,
}) => {
  const theme = useTheme();
  const center = {
    x: size / 2,
    y: size / 2,
  };
  const outerRadius = calculateOuterRadius(size);
  const innerRadius = calculateInnerRadius(size, thickness);

  const styles = mergeSx(
    {
      padding: collapseMargins ? 0 : `${DEFAULT_PADDING_AROUND_DONUT_CHART}px`,
      fill: getSkeletonColor(theme),
      animation: skeletonPulseAnimation,
    },
    sx
  );

  return (
    <Box
      component="svg"
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      xmlns="http://www.w3.org/2000/svg"
      sx={styles}
    >
      {/* Generate a donut shape, so that it works no matter the chart size */}
      <g transform={`translate(${center.x}, ${center.y})`}>
        <path
          d={`M0,-${outerRadius}A${outerRadius},${outerRadius},0,1,1,0,${outerRadius}A${outerRadius},${outerRadius},0,1,1,0,-${outerRadius}M0,-${innerRadius}A${innerRadius},${innerRadius},0,1,0,0,${innerRadius}A${innerRadius},${innerRadius},0,1,0,0,-${innerRadius}Z`}
        />
      </g>
    </Box>
  );
};
